/**
 * Provider capability model (Sprint 6A + 6B UMXP expansion).
 *
 * Host and UI must query capabilities — never `if (provider === "youtube")`.
 */
export var DEFAULT_CAPABILITIES = Object.freeze({
  // Transport
  supportsSeek: true,
  supportsPause: true,
  supportsResume: true,
  supportsVolume: true,
  supportsMute: true,
  supportsFullscreen: true,
  supportsPlaybackSpeed: true,
  // Tracks / narrative
  supportsSubtitles: false,
  supportsClosedCaptions: false,
  supportsMultipleAudioTracks: false,
  supportsChapters: true,
  supportsSkipIntro: true,
  supportsSkipCredits: true,
  // Quality / HDR
  supportsQualitySelection: false,
  supports4K: false,
  supportsHDR: false,
  supportsHEVC: false,
  supportsAV1: false,
  // Device
  supportsPiP: false,
  supportsCast: false,
  supportsAirPlay: false,
  // Live
  supportsLive: false,
  supportsLiveHls: false,
  supportsLiveDash: false,
  supportsLowLatencyHls: false,
  supportsDVR: false,
  supportsLiveRewind: false,
  supportsSimulive: false,
  supportsWebRTC: false,
  supportsEventCountdown: false,
  supportsScheduledBroadcast: false,
  supportsLiveFailover: false,
  // VOD container hints
  supportsProgressiveMp4: false,
  supportsHls: false,
  supportsDash: false,
  supportsAbr: false,
  // Security / interactivity
  supportsDRM: false,
  supportsInteractiveOverlays: false,
  supportsLiveChat: false,
  supportsPolls: false,
  supportsDonations: false,
  supportsMultiCamera: false,
  supportsAiChapters: false,
  supportsAiHighlights: false,
  // Ads (AVOD extension — capability only; logic in plugins)
  supportsAds: false,
  supportsPreRoll: false,
  supportsMidRoll: false,
  supportsPostRoll: false,
  supportsDynamicAdInsertion: false,
  /** Provider may show mandated chrome (YouTube/Vimeo ToS). */
  requiresNativeChrome: false,
  surfaceKind: "video",
});

/**
 * @param {object} [overrides]
 * @returns {object}
 */
export function createCapabilities(overrides) {
  return Object.assign({}, DEFAULT_CAPABILITIES, overrides || {});
}

/** Presets keyed by provider name — used when provider omits getCapabilities(). */
export var CAPABILITY_PRESETS = Object.freeze({
  progressive: createCapabilities({
    supportsProgressiveMp4: true,
    supportsSubtitles: true,
    supportsClosedCaptions: true,
    supportsMultipleAudioTracks: true,
    supportsPiP: true,
    supportsCast: true,
    supportsAirPlay: true,
    supportsQualitySelection: false,
    supports4K: true,
    supportsHDR: true,
    supportsHEVC: true,
    supportsAV1: true,
  }),
  "native-hls": createCapabilities({
    supportsHls: true,
    supportsAbr: true,
    supportsSubtitles: true,
    supportsClosedCaptions: true,
    supportsMultipleAudioTracks: true,
    supportsQualitySelection: true,
    supportsPiP: true,
    supportsCast: true,
    supportsAirPlay: true,
    supportsLive: true,
    supportsLiveHls: true,
    supportsLowLatencyHls: true,
    supportsDVR: true,
    supportsLiveRewind: true,
    supportsSimulive: true,
    supportsEventCountdown: true,
    supportsScheduledBroadcast: true,
    supportsLiveFailover: true,
    supports4K: true,
    supportsHDR: true,
    supportsHEVC: true,
    supportsDRM: true,
  }),
  "hls.js": createCapabilities({
    supportsHls: true,
    supportsAbr: true,
    supportsSubtitles: true,
    supportsClosedCaptions: true,
    supportsMultipleAudioTracks: true,
    supportsQualitySelection: true,
    supportsPiP: true,
    supportsCast: true,
    supportsLive: true,
    supportsLiveHls: true,
    supportsLowLatencyHls: true,
    supportsDVR: true,
    supportsLiveRewind: true,
    supportsSimulive: true,
    supports4K: true,
    supportsHEVC: true,
    supportsDRM: true,
  }),
  "youtube-embed": createCapabilities({
    supportsSubtitles: true,
    supportsClosedCaptions: true,
    supportsMultipleAudioTracks: false,
    supportsPlaybackSpeed: true,
    supportsQualitySelection: false,
    supportsLive: true,
    supports4K: true,
    // Element PiP N/A; Document PiP breaks cross-origin YouTube iframes
    supportsPiP: false,
    requiresNativeChrome: true,
    surfaceKind: "embed",
    supportsSeek: true,
  }),
  "vimeo-embed": createCapabilities({
    supportsSubtitles: true,
    supportsClosedCaptions: true,
    supportsMultipleAudioTracks: false,
    supportsPlaybackSpeed: true,
    supportsQualitySelection: false,
    // Element PiP N/A; Document PiP freezes Vimeo iframe rendering
    supportsPiP: false,
    requiresNativeChrome: true,
    surfaceKind: "embed",
  }),
});

/**
 * Human-readable capability matrix rows for docs / lab UI.
 * @returns {Array<{capability: string, progressive: boolean, hls: boolean, youtube: boolean, vimeo: boolean}>}
 */
export function buildCapabilityMatrixRows() {
  var keys = Object.keys(DEFAULT_CAPABILITIES).filter(function (k) {
    return k.indexOf("supports") === 0 || k === "requiresNativeChrome";
  });
  var progressive = CAPABILITY_PRESETS.progressive;
  var hls = CAPABILITY_PRESETS["hls.js"];
  var yt = CAPABILITY_PRESETS["youtube-embed"];
  var vim = CAPABILITY_PRESETS["vimeo-embed"];
  return keys.map(function (k) {
    return {
      capability: k,
      progressive: !!progressive[k],
      hls: !!hls[k],
      youtube: !!yt[k],
      vimeo: !!vim[k],
    };
  });
}

/**
 * Resolve capabilities for a provider instance.
 * Prefers provider.getCapabilities(); falls back to presets by name.
 *
 * @param {object|null} provider
 * @returns {object}
 */
export function resolveProviderCapabilities(provider) {
  if (!provider) {
    return createCapabilities({ supportsSeek: false, supportsPause: false });
  }
  var name = typeof provider.getName === "function" ? provider.getName() : "";
  var base = CAPABILITY_PRESETS[name]
    ? Object.assign({}, CAPABILITY_PRESETS[name])
    : createCapabilities();

  if (typeof provider.getSurfaceKind === "function") {
    base.surfaceKind = provider.getSurfaceKind();
  }

  if (typeof provider.getCapabilities === "function") {
    try {
      return createCapabilities(
        Object.assign({}, base, provider.getCapabilities() || {})
      );
    } catch (e) {
      return base;
    }
  }
  return base;
}

/**
 * Attach getCapabilities() to a provider if missing (uses preset).
 * @param {object} provider
 * @returns {object}
 */
export function withCapabilities(provider) {
  if (!provider || typeof provider.getCapabilities === "function") {
    return provider;
  }
  var caps = resolveProviderCapabilities(provider);
  provider.getCapabilities = function () {
    return Object.assign({}, caps);
  };
  return provider;
}

export default {
  DEFAULT_CAPABILITIES: DEFAULT_CAPABILITIES,
  createCapabilities: createCapabilities,
  CAPABILITY_PRESETS: CAPABILITY_PRESETS,
  resolveProviderCapabilities: resolveProviderCapabilities,
  withCapabilities: withCapabilities,
  buildCapabilityMatrixRows: buildCapabilityMatrixRows,
};
